import type { PlayerProfile, SlotState } from "@vire-craque/shared";

export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";

interface ErrorPayload {
  error?: { code?: string; message?: string };
}

export interface SessionPayload {
  id: string;
  status: string;
  profile: PlayerProfile;
  slots: SlotState[];
  expiresAt: number;
  orderId?: string;
  orderStatus?: string;
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body && !(init.body instanceof Blob) ? { "Content-Type": "application/json" } : {}),
      ...init.headers
    }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as ErrorPayload;
    throw new Error(payload.error?.message ?? `HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function createSession(): Promise<{
  id: string;
  csrfToken: string;
  expiresAt: number;
}> {
  return requestJson("/v1/preview-sessions", {
    method: "POST",
    body: JSON.stringify({ source: "direct_upload" })
  });
}

export async function exchangeFoto3x4Handoff(token: string): Promise<{
  id: string;
  csrfToken: string;
  inputAssetId: string;
  expiresAt: number;
}> {
  return requestJson("/v1/handoffs/foto3x4", {
    method: "POST",
    body: JSON.stringify({ token })
  });
}

export async function uploadPhoto(sessionId: string, csrfToken: string, file: File): Promise<void> {
  await requestJson(`/v1/preview-sessions/${sessionId}/photo`, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
      "X-CSRF-Token": csrfToken
    }
  });
}

export async function updateProfile(
  sessionId: string,
  csrfToken: string,
  profile: PlayerProfile
): Promise<void> {
  await requestJson(`/v1/preview-sessions/${sessionId}/profile`, {
    method: "PATCH",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify(profile)
  });
}

export async function startPreview(sessionId: string, csrfToken: string): Promise<void> {
  await requestJson(`/v1/preview-sessions/${sessionId}/start`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({})
  });
}

export async function getSession(sessionId: string): Promise<SessionPayload> {
  return requestJson(`/v1/preview-sessions/${sessionId}`);
}

export async function demoUnlock(sessionId: string, csrfToken: string): Promise<{ orderId: string }> {
  return requestJson(`/v1/preview-sessions/${sessionId}/demo-unlock`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({})
  });
}

export async function getOrder(orderId: string): Promise<SessionPayload> {
  return requestJson(`/v1/orders/${orderId}`);
}

export async function exchangeOrderAccess(orderId: string, token: string): Promise<void> {
  await requestJson("/v1/orders/access", {
    method: "POST",
    body: JSON.stringify({ orderId, token })
  });
}

export async function sendDeliveryEmail(
  orderId: string,
  csrfToken: string,
  email: string
): Promise<{ sent: boolean; debugAccessUrl?: string }> {
  return requestJson(`/v1/orders/${orderId}/delivery-email`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ email })
  });
}

export function assetUrl(assetId: string, download = false): string {
  return `${API_BASE}/v1/assets/${assetId}${download ? "?download=1" : ""}`;
}

export async function deleteOrder(orderId: string, csrfToken: string): Promise<void> {
  await requestJson(`/v1/orders/${orderId}`, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken },
    body: JSON.stringify({})
  });
}
